import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Link2, Loader2, Copy, Check, Trash2 } from "lucide-react";

const defaultForm = {
  slug: "",
  label: "",
  credits_target: "500",
  duration_days: "60",
  max_signups: "",
};

export function AdminSignupPromos() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/admin/signup-promos");
    setRows(r.data.promos || []);
  }, []);

  React.useEffect(() => {
    load().catch(() => toast.error("Promó linkek betöltése sikertelen"));
  }, [load]);

  const signupUrl = (slug) => {
    const base = window.location.origin.replace(/\/$/, "");
    return `${base}/belepes?promo=${encodeURIComponent(slug)}&register=1`;
  };

  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(signupUrl(slug));
      setCopied(slug);
      toast.success("Link másolva");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Másolás sikertelen");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/admin/signup-promos", {
        slug: form.slug.trim().toLowerCase(),
        label: form.label.trim() || undefined,
        credits_target: Number(form.credits_target) || 500,
        duration_days: Number(form.duration_days) || 60,
        max_signups: form.max_signups ? Number(form.max_signups) : null,
        active: true,
      });
      toast.success(
        r.data.reused_slug
          ? "Link frissítve (ugyanaz a slug újra használva — 0 regisztráció volt)"
          : "Regisztrációs link létrehozva",
      );
      setForm(defaultForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/signup-promos/${row.id}`, { active: !row.active });
      load();
    } catch {
      toast.error("Frissítés sikertelen");
    }
  };

  const remove = async (row) => {
    const used = row.signup_count ?? 0;
    const msg =
      used > 0
        ? `A „${row.slug}” linken már ${used} regisztráció van — csak szüneteltethető, nem törölhető.`
        : `Biztosan törlöd a „${row.slug}” promó linket? A slug ezután újra felhasználható.`;
    if (!window.confirm(msg)) return;
    if (used > 0) return;
    try {
      await api.delete(`/admin/signup-promos/${row.id}`);
      toast.success("Promó link törölve");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Törlés sikertelen");
    }
  };

  return (
    <div data-testid="admin-signup-promos" className="space-y-6">
      <div className="glass rounded-2xl p-5 border border-white/5">
        <h3 className="font-heading text-lg font-medium flex items-center gap-2 mb-1">
          <Link2 className="w-5 h-5 text-electric" /> Regisztrációs promó link
        </h3>
        <p className="text-xs text-slate-400 mb-4">
          A linken regisztráló <b>500 kreditet</b> kap (beállítható), és a megadott időszak alatt minden levonás után az egyenlege
          automatikusan visszatöltődik a célértékre (pl. 10-re csökken → azonnal 500).
        </p>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-slate-400">
            Link azonosító (slug)
            <input
              data-testid="signup-promo-slug"
              required
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="vip500"
              className="mt-1 w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm font-mono"
            />
          </label>
          <label className="text-xs text-slate-400">
            Megjelenő név
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Partner kampány"
              className="mt-1 w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm"
            />
          </label>
          <label className="text-xs text-slate-400">
            Cél egyenleg (kredit)
            <input
              type="number"
              min={1}
              value={form.credits_target}
              onChange={(e) => setForm({ ...form, credits_target: e.target.value })}
              className="mt-1 w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm"
            />
          </label>
          <label className="text-xs text-slate-400">
            Időszak (nap) — auto feltöltés
            <input
              type="number"
              min={1}
              value={form.duration_days}
              onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
              className="mt-1 w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm"
            />
          </label>
          <label className="text-xs text-slate-400 sm:col-span-2">
            Max. regisztráció (opcionális)
            <input
              type="number"
              min={1}
              value={form.max_signups}
              onChange={(e) => setForm({ ...form, max_signups: e.target.value })}
              className="mt-1 w-full bg-anthracite-900 border border-white/10 rounded p-2 text-sm"
            />
          </label>
          <div className="sm:col-span-2">
            <button
              type="submit"
              data-testid="signup-promo-create"
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric text-white text-sm disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Link létrehozása
            </button>
          </div>
        </form>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-400">Még nincs promó link.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={row.id} data-testid={`signup-promo-row-${row.id}`} className="glass rounded-2xl p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{row.label || row.slug}</div>
                  <div className="text-xs text-slate-400 font-mono mt-0.5">{row.slug}</div>
                  <div className="text-xs text-slate-500 mt-2">
                    {row.credits_target} kredit · {row.duration_days} nap feltöltés · {row.signup_count ?? 0} regisztráció
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid={`signup-promo-copy-${row.id}`}
                    onClick={() => copyLink(row.slug)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] text-xs hover:bg-white/10"
                  >
                    {copied === row.slug ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    Link másolása
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleActive(row)}
                    className={`px-3 py-1.5 rounded-lg text-xs border ${
                      row.active ? "border-emerald-400/40 text-emerald-300" : "border-white/10 text-slate-400"
                    }`}
                  >
                    {row.active ? "Aktív" : "Szünetel"}
                  </button>
                  <button
                    type="button"
                    data-testid={`signup-promo-delete-${row.id}`}
                    onClick={() => remove(row)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border border-red-400/30 text-red-300 hover:bg-red-500/10"
                    title={(row.signup_count ?? 0) > 0 ? "Már volt regisztráció — csak szüneteltetés" : "Link törlése"}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Törlés
                  </button>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-2 break-all">{signupUrl(row.slug)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
